import { useState } from "react";
import { Link } from "react-router-dom";
import { HeartHandshake, Mail } from "lucide-react";
import api, { apiError } from "../lib/api";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="forgot-password-page" className="min-h-screen bg-background flex flex-col">
      <header className="max-w-5xl w-full mx-auto px-4 py-4">
        <Link to="/" data-testid="home-link" className="flex items-center gap-2 w-fit">
          <span className="w-10 h-10 rounded-2xl bg-primary grid place-items-center">
            <HeartHandshake size={24} strokeWidth={2.5} className="text-white" />
          </span>
          <span className="text-xl font-extrabold tracking-tight">RoutineBuddy</span>
        </Link>
      </header>
      <main className="flex-1 flex items-start justify-center px-4 pt-8 pb-12">
        <div className="w-full max-w-md bg-white rounded-3xl border-2 border-foreground/10 p-8">
          {sent ? (
            <div data-testid="forgot-sent-message">
              <Mail size={40} strokeWidth={2} className="text-primary mb-4" />
              <h1 className="text-2xl font-extrabold">Check your inbox</h1>
              <p className="mt-2 text-lg text-muted-foreground">If that email has an account, we've sent a reset link. It works for 1 hour.</p>
              <Link to="/login" data-testid="back-to-login" className="mt-6 inline-block min-h-[56px] leading-[56px] px-6 rounded-full bg-primary text-white font-bold">Back to log in</Link>
            </div>
          ) : (
            <>
              <h1 className="text-2xl sm:text-3xl font-extrabold">Forgot your password?</h1>
              <p className="mt-2 text-lg text-muted-foreground">Enter your email and we'll send you a reset link.</p>
              <form onSubmit={submit} className="mt-7 space-y-4">
                <div>
                  <label htmlFor="email" className="block text-base font-bold mb-1">Email</label>
                  <input id="email" type="email" data-testid="forgot-email-input" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full min-h-[56px] rounded-2xl border-2 border-foreground/25 bg-white px-4 text-lg focus:outline-none focus:border-primary" placeholder="you@example.com" />
                </div>
                {error && <p data-testid="forgot-error" className="text-[#C53030] font-semibold bg-[#F56565]/10 border-2 border-[#F56565]/40 rounded-2xl px-4 py-3">{error}</p>}
                <button type="submit" data-testid="forgot-submit-button" disabled={busy} className="w-full min-h-[56px] rounded-full bg-primary text-white text-lg font-bold transition-transform duration-200 ease-out hover:-translate-y-0.5 disabled:opacity-60">
                  {busy ? "Sending…" : "Send reset link"}
                </button>
              </form>
              <Link to="/login" data-testid="back-to-login-link" className="mt-5 block min-h-[48px] text-primary font-bold text-base">Back to log in</Link>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
